import React, { useState } from 'react';
import { 
  Crown, 
  Star, 
  Zap, 
  Heart, 
  Shield, 
  Trophy, 
  Gem, 
  Sparkles, 
  Camera,
  Edit3,
  X,
  Check,
  Upload,
  User,
  Mic,
  Video,
  Award,
  Coins
} from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';

export interface UserBadge {
  id: string;
  type: 'badge' | 'frame' | 'indicator';
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  label: string;
  description: string;
  priority: number; // Higher number = higher priority
}

export interface AvatarWithBadgesProps {
  user?: any;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showBadges?: boolean;
  showFrames?: boolean;
  showIndicators?: boolean;
  onClick?: () => void;
  className?: string;
  editable?: boolean;
  onAvatarChange?: (file: File) => void;
}

// Badge definitions
export const USER_BADGES: Record<string, UserBadge> = {
  // Role-based badges
  'super-admin': {
    id: 'super-admin',
    type: 'badge',
    icon: Crown,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    label: 'Super Admin',
    description: 'Platform administrator',
    priority: 100
  },
  'healthcare-provider': {
    id: 'healthcare-provider',
    type: 'badge',
    icon: Shield,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'Healthcare Provider',
    description: 'Verified healthcare professional',
    priority: 90
  },
  'guru': {
    id: 'guru',
    type: 'badge',
    icon: Star,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    label: 'Guru',
    description: 'Health coach and educator',
    priority: 80
  },
  'lab-technician': {
    id: 'lab-technician',
    type: 'badge',
    icon: Gem,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    label: 'Lab Technician',
    description: 'Medical laboratory professional',
    priority: 70
  },

  // Achievement badges
  'early-adopter': {
    id: 'early-adopter',
    type: 'badge',
    icon: Sparkles,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    label: 'Early Adopter',
    description: 'Joined during beta phase',
    priority: 60
  },
  'supporter': {
    id: 'supporter',
    type: 'badge',
    icon: Heart,
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
    label: 'Supporter',
    description: 'Premium subscriber',
    priority: 50
  },
  'trusted': {
    id: 'trusted',
    type: 'badge',
    icon: Trophy,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    label: 'Trusted',
    description: 'High trust score',
    priority: 40
  },

  // Status indicators
  'live': {
    id: 'live',
    type: 'indicator',
    icon: Video,
    color: 'text-red-600',
    bgColor: 'bg-red-500',
    label: 'Live',
    description: 'Currently hosting a session',
    priority: 95
  },
  'online': {
    id: 'online',
    type: 'indicator',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-500',
    label: 'Online',
    description: 'Currently active',
    priority: 30
  },

  // Frames
  'premium-frame': {
    id: 'premium-frame',
    type: 'frame',
    icon: Crown,
    color: 'text-yellow-600',
    bgColor: 'bg-gradient-to-r from-yellow-400 to-orange-400',
    label: 'Premium',
    description: 'Premium subscriber frame',
    priority: 85
  },
  'guru-frame': {
    id: 'guru-frame',
    type: 'frame',
    icon: Star,
    color: 'text-purple-600',
    bgColor: 'bg-gradient-to-r from-purple-400 to-pink-400',
    label: 'Guru',
    description: 'Guru frame',
    priority: 75
  }
};

export const AvatarWithBadges: React.FC<AvatarWithBadgesProps> = ({
  user,
  size = 'md',
  showBadges = true,
  showFrames = true,
  showIndicators = true,
  onClick,
  className = '',
  editable = false,
  onAvatarChange
}) => {
  const { updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Get user badges based on role and achievements
  const getUserBadges = (): UserBadge[] => {
    if (!user) return [];

    const badges: UserBadge[] = [];

    // Role-based badges
    if (user.role === 'super_admin') badges.push(USER_BADGES['super-admin']);
    if (user.role === 'healthcare_provider') badges.push(USER_BADGES['healthcare-provider']);
    if (user.role === 'guru') badges.push(USER_BADGES['guru']);
    if (user.role === 'lab_technician') badges.push(USER_BADGES['lab-technician']);

    // Achievement badges
    if (user.is_early_adopter) badges.push(USER_BADGES['early-adopter']);
    if (user.is_supporter) badges.push(USER_BADGES['supporter']);
    if (user.trust_score >= 800) badges.push(USER_BADGES['trusted']);

    // Status indicators
    if (user.is_live) badges.push(USER_BADGES['live']);
    else if (user.is_online) badges.push(USER_BADGES['online']);

    // Frames
    if (user.is_supporter) badges.push(USER_BADGES['premium-frame']);
    if (user.role === 'guru') badges.push(USER_BADGES['guru-frame']);

    // Sort by priority (highest first)
    return badges.sort((a, b) => b.priority - a.priority);
  };

  const badges = getUserBadges();
  const frame = badges.find(b => b.type === 'frame');
  const indicators = badges.filter(b => b.type === 'indicator');
  const badgeList = badges.filter(b => b.type === 'badge');

  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const badgeSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };

  const indicatorSizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
    xl: 'w-5 h-5'
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      
      if (onAvatarChange) {
        onAvatarChange(file);
      } else {
        // Default avatar upload logic
        const formData = new FormData();
        formData.append('avatar', file);
        
        // You can implement your own avatar upload logic here
        // For now, we'll just update the profile
        await updateProfile({ avatar_url: URL.createObjectURL(file) });
      }
    } catch (error) {
      console.error('Failed to upload avatar:', error);
    } finally {
      setIsUploading(false);
      setIsEditing(false);
    }
  };

  const renderAvatar = () => {
    const avatarUrl = user?.avatar_url || user?.profile?.avatar_url;
    
    return (
      <div className={`relative ${sizeClasses[size]} ${className}`}>
        {/* Frame */}
        {showFrames && frame && (
          <div className={`absolute inset-0 rounded-full ${frame.bgColor} p-1`}>
            <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={`${user?.first_name || 'User'} avatar`}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className={`${sizeClasses[size]} text-gray-400`} />
              )}
            </div>
          </div>
        )}

        {/* Avatar without frame */}
        {(!showFrames || !frame) && (
          <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={`${user?.first_name || 'User'} avatar`}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className={`${sizeClasses[size]} text-gray-400`} />
            )}
          </div>
        )}

        {/* Status indicators */}
        {showIndicators && indicators.length > 0 && (
          <div className="absolute -bottom-1 -right-1 flex space-x-1">
            {indicators.map((indicator) => {
              const Icon = indicator.icon;
              return (
                <div
                  key={indicator.id}
                  className={`${indicatorSizeClasses[size]} rounded-full ${indicator.bgColor} flex items-center justify-center border-2 border-white`}
                  title={`${indicator.label}: ${indicator.description}`}
                >
                  <Icon className={`${indicatorSizeClasses[size]} ${indicator.color}`} />
                </div>
              );
            })}
          </div>
        )}

        {/* Edit button */}
        {editable && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute -top-1 -right-1 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
            title="Change avatar"
          >
            <Camera className="w-3 h-3" />
          </button>
        )}

        {/* Upload overlay */}
        {isEditing && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
            <div className="bg-white rounded-lg p-4 max-w-xs">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">Change Avatar</h3>
                <button
                  onClick={() => setIsEditing(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <label className="block w-full">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                <div className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                  {isUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">Choose Photo</span>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBadges = () => {
    if (!showBadges || badgeList.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {badgeList.slice(0, 3).map((badge) => {
          const Icon = badge.icon;
          return (
            <div
              key={badge.id}
              className={`${badgeSizeClasses[size]} ${badge.bgColor} ${badge.color} rounded-full flex items-center justify-center`}
              title={`${badge.label}: ${badge.description}`}
            >
              <Icon className={`${badgeSizeClasses[size]}`} />
            </div>
          );
        })}
        {badgeList.length > 3 && (
          <div className={`${badgeSizeClasses[size]} bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-xs font-medium`}>
            +{badgeList.length - 3}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`inline-flex flex-col items-center ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      {renderAvatar()}
      {renderBadges()}
    </div>
  );
}; 